import axios from 'axios';
import logger from '../utils/logger';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

// Helper functions
const handleApiError = (error) => {
  logger.error('API Error:', error);

  if (error.response) {
    // The request was made and the server responded with a status code
    // that falls out of the range of 2xx
    const errorData = error.response.data;

    // Handle validation errors
    if (error.response.status === 400 && errorData.errors && Array.isArray(errorData.errors)) {
      const validationMessages = errorData.errors.map(err => err.msg || err.message).join(', ');
      return {
        success: false,
        error: validationMessages || errorData.error || 'Validation failed',
        errors: errorData.errors,
        status: error.response.status
      };
    }

    return {
      success: false,
      error: errorData.error || errorData.message || 'An error occurred',
      status: error.response.status
    };
  } else if (error.request) {
    // The request was made but no response was received
    return {
      success: false,
      error: 'No response from server',
      status: 503
    };
  } else {
    // Something happened in setting up the request that triggered an Error
    return {
      success: false,
      error: error.message || 'Unknown error',
      status: 500
    };
  }
};

// CSRF token management
let csrfToken = null;

// Fetch CSRF token from backend
const fetchCsrfToken = async () => {
  try {
    const response = await axios.get(`${API_URL}/api/csrf-token`, {
      withCredentials: true
    });
    csrfToken = response.data.token;
    return csrfToken;
  } catch (error) {
    logger.error('Failed to fetch CSRF token:', error);
    return null;
  }
};

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Send cookies with requests
});

// HTTP-only cookies are sent automatically with withCredentials: true
// CSRF token is sent in x-csrf-token header for state-changing requests

// Request interceptor to add CSRF token to state-changing requests
api.interceptors.request.use(
  async (config) => {
    // Only add CSRF token to state-changing methods
    const stateChangingMethods = ['POST', 'PUT', 'DELETE', 'PATCH'];
    if (stateChangingMethods.includes(config.method.toUpperCase())) {
      // Fetch token if not already cached
      if (!csrfToken) {
        await fetchCsrfToken();
      }
      // Add CSRF token to header
      if (csrfToken) {
        config.headers['x-csrf-token'] = csrfToken;
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle CSRF token expiration and auth errors
api.interceptors.response.use(
  (response) => {
    // Update CSRF token from response cookie if present
    // The backend may set a new CSRF token in the response
    const setCookieHeader = response.headers['set-cookie'];
    if (setCookieHeader) {
      const csrfCookie = setCookieHeader.find(cookie => cookie.startsWith('_csrf='));
      if (csrfCookie) {
        const tokenMatch = csrfCookie.match(/_csrf=([^;]+)/);
        if (tokenMatch && tokenMatch[1]) {
          csrfToken = tokenMatch[1];
        }
      }
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Handle CSRF token errors - silently refresh and retry
    if (error.response && error.response.status === 403) {
      const errorMessage = error.response.data?.error || '';

      // CSRF token validation failed - refresh token and retry silently
      if (errorMessage.includes('CSRF') || errorMessage.includes('Session expired')) {
        // Clear cached token and fetch a new one
        csrfToken = null;
        await fetchCsrfToken();

        // Retry the request with the new token (only once)
        if (!originalRequest._retry && csrfToken) {
          originalRequest._retry = true;
          originalRequest.headers['x-csrf-token'] = csrfToken;
          try {
            return await api.request(originalRequest);
          } catch (retryError) {
            // If retry also fails, don't show CSRF error to user
            // Just return a generic error
            return Promise.reject({
              ...retryError,
              response: {
                ...retryError.response,
                data: {
                  success: false,
                  error: 'Please refresh the page and try again'
                }
              }
            });
          }
        }
      }
      // Don't logout on 403 - these are usually permission/CSRF issues, not session expiration
    }

    // Handle 401 Unauthorized - only logout if backend explicitly indicates session expiration
    if (error.response && error.response.status === 401) {
      const errorMessage = error.response.data?.error || '';

      // Only logout on specific error messages that indicate session expiration:
      // - "Not authorized, token failed" - JWT token expired/invalid
      // - "Not authorized, no token" - Token missing (session expired)
      const sessionExpiredErrors = [
        'Not authorized, token failed',
        'Not authorized, no token',
        'token failed',
        'token expired'
      ];

      const isSessionExpired = sessionExpiredErrors.some(msg =>
        errorMessage.toLowerCase().includes(msg.toLowerCase())
      );

      // Don't logout if:
      // - Already on login/register pages
      // - It's a login/register request
      // - Error is not session expiration related
      if (isSessionExpired &&
        window.location.pathname !== '/login' &&
        window.location.pathname !== '/register' &&
        !originalRequest.url.includes('/login') &&
        !originalRequest.url.includes('/register') &&
        !originalRequest.url.includes('/register')) {
        const event = new CustomEvent('auth:logout', { detail: { reason: 'Session expired' } });
        window.dispatchEvent(event);
      }
    }

    return Promise.reject(error);
  }
);

// Note: CSRF token is now lazily loaded on first request instead of during module initialization
// This prevents "insecure operation" errors in development environments

// Admin authentication services
export const adminService = {
  // Login admin
  login: async (email, password) => {
    try {
      const response = await api.post('/api/admin/login', { email, password });
      // HTTP-only cookies are set automatically by the backend
      return response.data;
    } catch (error) {
      throw error.response ? error.response.data : { error: 'Network error' };
    }
  },

  // Get admin profile
  getProfile: async () => {
    try {
      const response = await api.get('/api/admin/profile');
      return response.data;
    } catch (error) {
      throw error.response ? error.response.data : { error: 'Network error' };
    }
  },

  // Logout admin
  logout: async () => {
    try {
      await api.post('/api/admin/logout');
    } catch (error) {
      // Even if logout fails, we'll clear client state
      console.error('Logout error:', error);
    }
  },

  // Check if admin is authenticated (will make a request that uses cookies)
  isAuthenticated: async () => {
    try {
      await api.get('/api/admin/profile');
      return true;
    } catch (error) {
      return false;
    }
  },
};

// User authentication services
export const userService = {
  // Register user
  register: async (userData) => {
    try {
      const response = await api.post('/api/users/register', userData);
      // HTTP-only cookies are set automatically by the backend
      return response.data;
    } catch (error) {
      throw error.response ? error.response.data : { error: 'Network error' };
    }
  },

  // Login user
  login: async (credentials) => {
    try {
      const response = await api.post('/api/users/login', credentials);
      // HTTP-only cookies are set automatically by the backend
      return response.data;
    } catch (error) {
      throw error.response ? error.response.data : { error: 'Network error' };
    }
  },

  // Get user profile
  getProfile: async () => {
    try {
      const response = await api.get('/api/users/profile');
      return response.data;
    } catch (error) {
      throw error.response ? error.response.data : { error: 'Network error' };
    }
  },

  // Update user profile
  updateProfile: async (userData) => {
    try {
      const response = await api.put('/api/users/profile', userData);
      // HTTP-only cookies handle authentication automatically
      return response.data;
    } catch (error) {
      throw error.response ? error.response.data : { error: 'Network error' };
    }
  },

  // Address management
  addresses: {
    add: async (addressData) => {
      try {
        const response = await api.post('/api/users/addresses', addressData);
        return response.data;
      } catch (error) {
        throw error.response ? error.response.data : { error: 'Network error' };
      }
    },

    update: async (addressId, addressData) => {
      try {
        const response = await api.put(`/api/users/addresses/${addressId}`, addressData);
        return response.data;
      } catch (error) {
        throw error.response ? error.response.data : { error: 'Network error' };
      }
    },

    delete: async (addressId) => {
      try {
        const response = await api.delete(`/api/users/addresses/${addressId}`);
        return response.data;
      } catch (error) {
        throw error.response ? error.response.data : { error: 'Network error' };
      }
    }
  },

  // Wishlist management
  wishlist: {
    get: async () => {
      try {
        const response = await api.get('/api/users/wishlist');
        return response.data;
      } catch (error) {
        throw error.response ? error.response.data : { error: 'Network error' };
      }
    },

    add: async (productId) => {
      try {
        const response = await api.post(`/api/users/wishlist/${productId}`);
        return response.data;
      } catch (error) {
        throw error.response ? error.response.data : { error: 'Network error' };
      }
    },

    remove: async (productId) => {
      try {
        const response = await api.delete(`/api/users/wishlist/${productId}`);
        return response.data;
      } catch (error) {
        throw error.response ? error.response.data : { error: 'Network error' };
      }
    }
  },

  // Logout user
  logout: async () => {
    try {
      await api.post('/api/users/logout');
    } catch (error) {
      // Even if logout fails, we'll clear client state
      console.error('Logout error:', error);
    }
  },

  // Check if user is authenticated (will make a request that uses cookies)
  isAuthenticated: async () => {
    try {
      await api.get('/api/users/profile');
      return true;
    } catch (error) {
      return false;
    }
  },

  // Get current user (fetch from API since we don't store locally)
  getCurrentUser: async () => {
    try {
      const response = await api.get('/api/users/profile');
      return response.data.user;
    } catch (error) {
      return null;
    }
  }
};

// User API calls
export const registerUser = async (userData, referralRef) => {
  try {
    const url = referralRef
      ? `/api/users/register?ref=${encodeURIComponent(referralRef)}`
      : '/api/users/register';
    const response = await api.post(url, userData);
    return response.data;
  } catch (error) {
    return handleApiError(error);
  }
};

export const loginUser = async (credentials) => {
  try {
    const response = await api.post('/api/users/login', credentials);
    return response.data;
  } catch (error) {
    // Check if this is a verification error
    if (error.response && error.response.status === 403 && error.response.data.requiresVerification) {
      return {
        success: false,
        error: error.response.data.error,
        requiresVerification: true,
        message: error.response.data.message
      };
    }
    return handleApiError(error);
  }
};

export const verifyEmail = async (verificationData) => {
  try {
    const response = await api.post('/api/users/verify-email', verificationData);
    return response.data;
  } catch (error) {
    return handleApiError(error);
  }
};

export const resendVerification = async (email) => {
  try {
    const response = await api.post('/api/users/resend-verification', { email });
    return response.data;
  } catch (error) {
    return handleApiError(error);
  }
};

export const getUserProfile = async () => {
  try {
    const response = await api.get('/api/users/profile');
    return response.data;
  } catch (error) {
    return handleApiError(error);
  }
};

export const updateUserProfile = async (userData) => {
  try {
    const response = await api.put('/api/users/profile', userData);
    return response.data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Address API calls
export const getAddresses = async () => {
  try {
    const response = await api.get('/api/users/addresses');
    return response.data;
  } catch (error) {
    return handleApiError(error);
  }
};

export const addAddress = async (addressData) => {
  try {
    const response = await api.post('/api/users/addresses', addressData);
    return response.data;
  } catch (error) {
    return handleApiError(error);
  }
};

export const updateAddress = async (addressId, addressData) => {
  try {
    const response = await api.put(`/api/users/addresses/${addressId}`, addressData);
    return response.data;
  } catch (error) {
    return handleApiError(error);
  }
};

export const deleteAddress = async (addressId) => {
  try {
    const response = await api.delete(`/api/users/addresses/${addressId}`);
    return response.data;
  } catch (error) {
    return handleApiError(error);
  }
};

export const setDefaultAddress = async (addressId) => {
  try {
    const response = await api.put(`/api/users/addresses/${addressId}/default`);
    return response.data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Wishlist API calls
export const getWishlist = async () => {
  try {
    const response = await api.get('/api/users/wishlist');
    return response.data;
  } catch (error) {
    return handleApiError(error);
  }
};

export const addToWishlist = async (productId) => {
  try {
    const response = await api.post('/api/users/wishlist', { productId });
    return response.data;
  } catch (error) {
    return handleApiError(error);
  }
};

export const removeFromWishlist = async (productId) => {
  try {
    const response = await api.delete(`/api/users/wishlist/${productId}`);
    return response.data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Order API calls
export const createOrder = async (orderData) => {
  try {
    const response = await api.post('/api/orders', orderData);
    return response.data;
  } catch (error) {
    return handleApiError(error);
  }
};

export const createCheckoutSession = async (orderId, amount, currency = 'gbp') => {
  try {
    const response = await api.post('/api/payment/create-checkout-session', {
      orderId,
      amount,
      currency
    });
    return response.data;
  } catch (error) {
    return handleApiError(error);
  }
};

export const verifyCheckout = async (sessionId, orderId) => {
  try {
    const response = await api.post('/api/payment/verify-checkout', {
      sessionId,
      orderId
    });
    return response.data;
  } catch (error) {
    return handleApiError(error);
  }
};

export const getMyOrders = async (params) => {
  try {
    const response = await api.get('/api/orders/myorders', { params });
    return response.data;
  } catch (error) {
    return handleApiError(error);
  }
};

export const getOrderDetails = async (orderNumber) => {
  try {
    const response = await api.get(`/api/orders/${orderNumber}`);
    return response.data;
  } catch (error) {
    return handleApiError(error);
  }
};

export const requestOrderReturn = async (orderNumber, reason) => {
  try {
    const response = await api.post(`/api/orders/${orderNumber}/return`, { reason });
    return response.data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Coin Activity API calls
export const getCoinActivity = async () => {
  try {
    const response = await api.get('/api/coins/activity');
    return response.data;
  } catch (error) {
    return handleApiError(error);
  }
};

export const applyGiftCard = async (code) => {
  try {
    const response = await api.post('/api/cart/apply-gift-card', { code });
    return response.data;
  } catch (error) {
    return handleApiError(error);
  }
};

export const submitSupportRequest = async (supportData) => {
  try {
    const response = await api.post('/api/email/support', supportData);
    return response.data;
  } catch (error) {
    return handleApiError(error);
  }
};

export const purchaseGiftCard = async (giftCardData) => {
  try {
    const response = await api.post('/api/giftcards/purchase', giftCardData);
    return response.data;
  } catch (error) {
    return handleApiError(error);
  }
};

export const confirmGiftCardPurchase = async (sessionId) => {
  try {
    const response = await api.post('/api/giftcards/confirm-purchase', { sessionId });
    return response.data;
  } catch (error) {
    return handleApiError(error);
  }
};

export const requestPasswordChangeOTP = async () => {
  try {
    const response = await api.post('/api/users/request-password-change-otp');
    return response.data;
  } catch (error) {
    return handleApiError(error);
  }
};

export const changePassword = async (otp, newPassword) => {
  try {
    const response = await api.post('/api/users/change-password', { otp, newPassword });
    return response.data;
  } catch (error) {
    return handleApiError(error);
  }
};

export const validateResetToken = async (token) => {
  try {
    const response = await api.get(`/api/users/reset-password/${token}`);
    return response.data;
  } catch (error) {
    return handleApiError(error);
  }
};

export const resetPassword = async (token, password) => {
  try {
    const response = await api.post(`/api/users/reset-password/${token}`, { password });
    return response.data;
  } catch (error) {
    return handleApiError(error);
  }
};

export default api;
